import type { FC, ReactElement } from 'react'
import { useState, useEffect } from 'react'

import type { WhiteLabel } from '@skytech/types'
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import { twJoin } from 'tailwind-merge'

import { Button } from '../Button'

import ProgressStep from './ProgressStep'
import type { WizardStepArgs } from './WizardStep'

import './wizard.scss'

export const Wizard: FC<{
  children: ReactElement<WizardStepArgs> | ReactElement<WizardStepArgs>[]
  initialStep?: number
  previousButtonTextDefault: string | ReactElement
  skipButtonTextDefault: string | ReactElement
  nextButtonTextDefault: string | ReactElement
  skipNextButtonOnFinalStep?: boolean
  isBigNavigationMarginTop?: boolean
  isLoading?: boolean
  className?: { progressBar?: string; steps?: string }
  onIsWizardModeSet: (v: boolean) => void
  onComplete: () => void
  onStepChange?: (nextStep: number, nextFurthestStep: number) => void
}> = ({
  children,
  previousButtonTextDefault,
  skipButtonTextDefault,
  nextButtonTextDefault,
  initialStep = 0,
  skipNextButtonOnFinalStep,
  isBigNavigationMarginTop,
  isLoading,
  className,
  onComplete,
  onStepChange,
  onIsWizardModeSet,
}) => {
  const [activeStep, setActiveStep] = useState(initialStep)
  const [furthestStep, setFurthestStep] = useState(initialStep)

  const wizardChildren: ReactElement<WizardStepArgs>[] = Array.isArray(children)
    ? children.flat()
    : [children]
  // Some wizard children are a collection of wizard steps wrapped in a fragment
  const flattenedChildren = wizardChildren.flatMap(ch =>
    Array.isArray(ch.props.children) ? ch.props.children : ch
  )
  const visibleChildren = flattenedChildren.filter(({ props }) => !props.isHidden)
  const stepCount = visibleChildren.length

  useEffect(() => {
    setActiveStep(initialStep)
    setFurthestStep(initialStep)
  }, [initialStep])

  useEffect(() => {
    onIsWizardModeSet(true)
  }, [])

  useEffect(
    () => () => {
      onIsWizardModeSet(false)
    },
    []
  )

  const {
    children: activeStepChildren,
    previousButtonText = previousButtonTextDefault,
    skipButtonText = skipButtonTextDefault,
    nextButtonText = nextButtonTextDefault,
    displayNavigation = true,
    isSkippable = false,
    isFinalStep = activeStep === stepCount - 1,
    isValid,
  } = visibleChildren[activeStep]?.props || {}

  const changeStepTo = (nextStep: number) => {
    const nextFurthestStep = furthestStep < nextStep ? nextStep : furthestStep

    setActiveStep(nextStep)
    setFurthestStep(nextFurthestStep)
    if (onStepChange) onStepChange(nextStep, nextFurthestStep)
  }

  const advanceWizard = () => {
    const nextStep = activeStep + 1

    if (isFinalStep) {
      onComplete()
    } else {
      changeStepTo(nextStep)
    }
  }

  const regressWizard = () => {
    changeStepTo(activeStep - 1)
  }

  const Step = activeStepChildren.type

  // todo: replace with the org from url when ready
  const whiteLabel = localStorage.getItem('org') as WhiteLabel

  return (
    <div className={`ui-wizard ${whiteLabel}`}>
      <ol className={twJoin('ui-wizard__progress', className?.progressBar)}>
        {visibleChildren.map((step, index) => {
          const { name, isStarted, isValid } = step.props

          return (
            <ProgressStep
              key={index}
              index={index}
              name={name}
              isActive={activeStep === index}
              isValid={isValid}
              isStarted={isStarted || index <= furthestStep}
              isDisabled={isLoading}
              onClick={() => changeStepTo(index)}
            />
          )
        })}
      </ol>

      <div className={twJoin('ui-wizard__steps', className?.steps)}>
        <Step
          advanceWizard={advanceWizard}
          regressWizard={regressWizard}
          onActiveStepSet={(newActiveStep: number) => setActiveStep(newActiveStep)}
          {...activeStepChildren.props}
        />
      </div>

      {displayNavigation && (
        <div className={`navigation ${isBigNavigationMarginTop ? 'is-big-top-margin' : ''}`}>
          <div className="buttons">
            <Button
              onClick={regressWizard}
              disabled={activeStep === 0 || isLoading}
              Icon={<ChevronLeftIcon />}
            >
              {previousButtonText}
            </Button>

            {isSkippable && !isFinalStep && (
              <Button onClick={() => changeStepTo(activeStep + 1)}>{skipButtonText}</Button>
            )}

            {skipNextButtonOnFinalStep && isFinalStep ? null : (
              <Button
                isIconRight={!isFinalStep}
                variant={isFinalStep ? 'primary' : 'secondary'}
                disabled={!isValid || isLoading}
                Icon={isFinalStep ? undefined : <ChevronRightIcon />}
                isLoading={isLoading}
                onClick={advanceWizard}
              >
                {nextButtonText}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export * from './WizardStep'
